'use client';

import { Component, type ReactNode } from 'react';
import { installGlobalErrorReporting, reportClientError, setErrorReportApp } from './client-errors';

/**
 * Catches a render-time crash so a single broken component shows a small notice
 * instead of a blank page, and reports it to Slack on the way. Also installs the
 * window-level handlers (uncaught errors, unhandled rejections) once, so the two
 * kinds of front-end failure (a bad render and a stray async throw) both land
 * in the same channel as the API's 5xx alerts.
 *
 * A class component because componentDidCatch has no hook equivalent.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; app?: string; fallback?: ReactNode },
  { crashed: boolean }
> {
  state = { crashed: false };

  constructor(props: { children: ReactNode; app?: string; fallback?: ReactNode }) {
    super(props);
    if (props.app) setErrorReportApp(props.app);
    installGlobalErrorReporting();
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error: Error) {
    reportClientError({ message: error.message, stack: error.stack, source: 'render' });
  }

  render() {
    if (this.state.crashed) {
      return (
        this.props.fallback ?? (
          <div className="boundary-note">
            Something on this page broke. It has been reported. Try reloading.
          </div>
        )
      );
    }
    return this.props.children;
  }
}
